import { useIntl } from 'react-intl';
import { useEffect } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { Popover, Typography } from 'antd';
import { currencyFormatter, metricPrefixFormatter } from '@/utils/number';
import { ContentWrapper, EllipsisButton, Group } from '@/components/_common';

import { useChartTheme, useConvertChartData, useDisclosure } from '@/hooks';

import moment from 'moment';
import InvoicingBillingXPaymentsXReceiptsModal from './InvoicingBillingXPaymentsXReceiptsModal';
import { useChartBillingExpenseRevenue } from '@/core/domain/workorder/ManagementCharts/ManagementChart.hooks';
import { DateTimeFormat } from '@/utils/moment';
import {
  BillingExpenseRevenueFormValues,
  BillingExpenseRevenueParams,
} from '@/core/domain/workorder/ManagementCharts/ManagementChart.types';
import { Info } from 'react-feather';

const { Title, Paragraph } = Typography;

const defaultStartDate = moment().subtract(5, 'months').startOf('day');
const defaultEndDate = moment().endOf('day');

function InvoicingBillingXPaymentsXReceipts() {
  const intl = useIntl();
  const chartTheme = useChartTheme();
  const [modalVisible, modalVisibleAction] = useDisclosure();

  const chartBillingExpenseRevenue = useChartBillingExpenseRevenue();

  const chartData = useConvertChartData(
    chartBillingExpenseRevenue.data?.chartData,
  );

  useEffect(() => {
    chartBillingExpenseRevenue.mutateAsync({
      startDate: defaultStartDate.startOf('day').format(DateTimeFormat),
      endDate: defaultEndDate.endOf('day').format(DateTimeFormat),
    });
  }, []);

  const handleOk = async (values: BillingExpenseRevenueFormValues) => {
    try {
      const obj: BillingExpenseRevenueParams = {
        ...values,
        companiesIds: values.companiesIds,
        startDate: values.startDate.startOf('day').format(DateTimeFormat),
        endDate: values.endDate.endOf('day').format(DateTimeFormat),
      };

      await chartBillingExpenseRevenue.mutateAsync(obj);

      modalVisibleAction.close();
    } catch {}
  };

  const items = [
    {
      label: 'Faturamento',
      desc: '(Total OS - Peças fornecidas) + Total VD',
    },
    {
      label: 'Pagamentos',
      desc: 'Contas a pagar liquidado',
    },
    {
      label: 'Recebimentos',
      desc: 'Contas a receber liquidados',
    },
  ];

  return (
    <>
      <ContentWrapper
        style={{ height: '430px', padding: '8px' }}
        extra={
          <Group>
            <Popover
              title="Faturamento x Pagamentos x Recebimentos"
              placement="right"
              content={
                <div>
                  {items.map((item, index) => (
                    <p key={index}>
                      <strong>{item.label}:</strong> {item.desc}
                    </p>
                  ))}
                </div>
              }
            >
              <Info size={18} color="#364068" />
            </Popover>

            <EllipsisButton onClick={modalVisibleAction.open} />
          </Group>
        }
      >
        <Title level={5} style={{ textAlign: 'center', marginBottom: '4px' }}>
          Faturamento x Pagamentos x Recebimentos
        </Title>

        <Paragraph style={{ textAlign: 'center', margin: '4px 0 4px 0' }}>
          {`Período: ${moment(
            chartBillingExpenseRevenue.variables?.startDate,
          ).format('DD/MM/YYYY')} a ${moment(
            chartBillingExpenseRevenue.variables?.endDate,
          ).format('DD/MM/YYYY')}`}
        </Paragraph>

        <ResponsiveLine
          data={chartData ? chartData : []}
          enableSlices="x"
          margin={{ top: 15, right: 25, bottom: 90, left: 70 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 0,
            max: 'auto',
            reverse: false,
          }}
          yFormat={(value) => `${currencyFormatter(value)}`}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 7,
            tickPadding: 10,
            tickRotation: 0,
            legendOffset: 36,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            tickValues: 5,
            legend: intl.formatMessage({
              id: 'dashboard.averageTicketReport.averageTicketPartsAndServices',
            }),
            format: (value) => `${metricPrefixFormatter(+value)}`,
            legendOffset: -50,
            legendPosition: 'middle',
          }}
          enableGridX={false}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh
          theme={chartTheme}
        />
      </ContentWrapper>

      <InvoicingBillingXPaymentsXReceiptsModal
        open={modalVisible}
        handleOk={handleOk}
        onCancel={modalVisibleAction.close}
      />
    </>
  );
}

export default InvoicingBillingXPaymentsXReceipts;
